import { useEffect, useState } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import {
	classifyLinkedJiraWorkItemsError,
	fetchLinkedJiraWorkItemUrls,
	isLinkedJiraWorkItemsClientError,
} from '../utils/fetchLinkedJiraWorkItemUrls.ts';
import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

import { getIncomingLinksRemoteLinksFailedEvent } from './incomingLinksAnalytics.ts';

// Bounds the best-effort Jira remote-link fallback so a stuck app-link call is reported as a timeout
// instead of merging minutes later.
const REMOTE_LINKS_TIMEOUT_MS = 5000;

export type RemoteLinksResult =
	| { status: 'resolved'; fetched: number; links: EnhancedSmartLink[] }
	| { status: 'failed' };

export type UseIncomingLinksRemoteLinksOptions = {
	skip: boolean;
	hydrateLinks: (urls: string[]) => Promise<EnhancedSmartLink[]>;
};

export const useIncomingLinksRemoteLinks = (
	contentId: string | undefined,
	{ skip, hydrateLinks }: UseIncomingLinksRemoteLinksOptions,
): RemoteLinksResult | null => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const [remoteLinksResult, setRemoteLinksResult] = useState<RemoteLinksResult | null>(null);

	useEffect(() => {
		if (skip || !contentId) {
			return;
		}

		let cancelled = false;
		let timedOut = false;
		const controller = new AbortController();
		const timer = setTimeout(() => {
			timedOut = true;
			controller.abort();
		}, REMOTE_LINKS_TIMEOUT_MS);

		fetchLinkedJiraWorkItemUrls(contentId, controller.signal)
			.then(async (urls) => {
				// The deadline covers the REST call only; hydration has ORS's own failure handling.
				clearTimeout(timer);
				return {
					fetched: urls.length,
					links: urls.length > 0 ? await hydrateLinks(urls) : [],
				};
			})
			.then(({ fetched, links: hydrated }) => {
				if (!cancelled) {
					setRemoteLinksResult({ status: 'resolved', fetched, links: hydrated });
				}
			})
			.catch((err: unknown) => {
				if (cancelled) {
					return;
				}
				// 4xx is expected for anonymous viewers and sites without a Jira app link, and a
				// timeout is our own deadline; neither is worth a monitoring report.
				if (!isLinkedJiraWorkItemsClientError(err) && !timedOut) {
					const error = err instanceof Error ? err : new Error(String(err));
					getMonitoringClient().submitError(error, { attribution: Attribution.INTEGRATIONS });
				}
				createEvent(
					getIncomingLinksRemoteLinksFailedEvent({
						errorType: classifyLinkedJiraWorkItemsError(err, timedOut),
					}),
				).fire();
				setRemoteLinksResult({ status: 'failed' });
			});

		return () => {
			cancelled = true;
			clearTimeout(timer);
			controller.abort();
		};
	}, [skip, contentId, hydrateLinks, createEvent]);

	return remoteLinksResult;
};
